import type { Product, PublicStore, TemplateDetail } from "../../lib/api";

function mockProduct(item: Partial<Product> & { id: string; slug: string; name: string; priceMinor: number }): Product {
  return {
    description: "Carefully designed and crafted with premium materials for everyday living.",
    collection: "Featured",
    compareAtMinor: null,
    images: [],
    options: [],
    stock: 24,
    status: "active",
    position: 0,
    createdAt: "2026-10-01T10:00:00Z",
    updatedAt: "2026-10-01T10:00:00Z",
    ...item,
  };
}

const APPAREL_PRODUCTS: Product[] = [
  mockProduct({
    id: "sp-app-1",
    slug: "relaxed-linen-shirt",
    name: "Relaxed Linen Shirt",
    description: "Cut from breathable, washed European linen with clean mother-of-pearl buttons. Relaxed silhouette.",
    priceMinor: 8800,
    compareAtMinor: 11000,
    collection: "Apparel",
    images: [
      { url: "https://images.unsplash.com/photo-1596755094514-f87e34085b2c?auto=format&fit=crop&w=800&q=80", alt: "Relaxed Linen Shirt" },
      { url: "https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?auto=format&fit=crop&w=800&q=80", alt: "Linen Shirt Detail" },
    ],
    options: [{ name: "Size", values: ["XS", "S", "M", "L", "XL"] }, { name: "Color", values: ["Sand", "Olive", "White"] }],
    position: 0,
  }),
  mockProduct({
    id: "sp-app-2",
    slug: "pleated-wool-trousers",
    name: "Pleated Wool Trousers",
    description: "High-waisted trousers with double pleats and tapered legs crafted from mid-weight virgin wool.",
    priceMinor: 14500,
    collection: "Apparel",
    images: [
      { url: "https://images.unsplash.com/photo-1509551388413-e18d0ac5d495?auto=format&fit=crop&w=800&q=80", alt: "Pleated Wool Trousers" },
    ],
    options: [{ name: "Size", values: ["28", "30", "32", "34"] }],
    position: 1,
  }),
  mockProduct({
    id: "sp-app-3",
    slug: "weatherproof-canvas-tote",
    name: "Weatherproof Canvas Tote",
    description: "Heavy-duty 18oz water-resistant duck canvas with vegetable-tanned bridle leather handles and brass rivets.",
    priceMinor: 12500,
    compareAtMinor: 15000,
    collection: "Accessories",
    images: [
      { url: "https://images.unsplash.com/photo-1544816155-12df9643f363?auto=format&fit=crop&w=800&q=80", alt: "Weatherproof Canvas Tote" },
    ],
    options: [{ name: "Color", values: ["Tan", "Navy", "Charcoal"] }],
    position: 2,
  }),
  mockProduct({
    id: "sp-app-4",
    slug: "minimal-leather-billfold",
    name: "Minimal Leather Billfold",
    description: "Slim bifold wallet made from Italian full-grain leather that patinas beautifully over time.",
    priceMinor: 5500,
    collection: "Accessories",
    images: [
      { url: "https://images.unsplash.com/photo-1627123424574-724758594e93?auto=format&fit=crop&w=800&q=80", alt: "Minimal Leather Billfold" },
    ],
    options: [{ name: "Color", values: ["Cognac", "Black"] }],
    position: 3,
  }),
  mockProduct({
    id: "sp-app-5",
    slug: "structured-cotton-blazer",
    name: "Structured Cotton Blazer",
    description: "Unstructured shoulders and tailored body in breathable cotton twill for modern versatile styling.",
    priceMinor: 19500,
    collection: "Apparel",
    images: [
      { url: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=800&q=80", alt: "Cotton Blazer" },
    ],
    options: [{ name: "Size", values: ["38R", "40R", "42R"] }],
    position: 4,
  }),
  mockProduct({
    id: "sp-app-6",
    slug: "handcrafted-leather-loafers",
    name: "Handcrafted Leather Loafers",
    description: "Blake-stitched Italian calfskin loafers with cushioned insoles and natural leather soles.",
    priceMinor: 18000,
    collection: "Footwear",
    images: [
      { url: "https://images.unsplash.com/photo-1533867617858-e7b97e060509?auto=format&fit=crop&w=800&q=80", alt: "Leather Loafers" },
    ],
    options: [{ name: "Size", values: ["40", "41", "42", "43", "44"] }],
    position: 5,
  }),
];

const BEAUTY_PRODUCTS: Product[] = [
  mockProduct({
    id: "sp-bt-1",
    slug: "radiance-vitamin-c-serum",
    name: "Radiance Vitamin C Serum",
    description: "Stabilized 15% L-Ascorbic Acid with Vitamin E and Ferulic Acid to brighten tone and protect against environmental stress.",
    priceMinor: 4800,
    compareAtMinor: 6000,
    collection: "Serums",
    images: [
      { url: "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=800&q=80", alt: "Vitamin C Serum" },
    ],
    options: [{ name: "Size", values: ["30ml", "50ml"] }],
    position: 0,
  }),
  mockProduct({
    id: "sp-bt-2",
    slug: "hydrating-dew-essence",
    name: "Hydrating Dew Essence",
    description: "Multi-weight Hyaluronic Acid infused with fermented green tea water for deep, weightless hydration.",
    priceMinor: 3800,
    collection: "Essences",
    images: [
      { url: "https://images.unsplash.com/photo-1608248597359-5975d0506eb6?auto=format&fit=crop&w=800&q=80", alt: "Hydrating Dew Essence" },
    ],
    options: [{ name: "Size", values: ["100ml"] }],
    position: 1,
  }),
  mockProduct({
    id: "sp-bt-3",
    slug: "botanical-cleansing-balm",
    name: "Botanical Cleansing Balm",
    description: "Gentle oil-to-milk balm that melts away makeup and impurities while nourishing the skin barrier.",
    priceMinor: 4200,
    collection: "Cleansers",
    images: [
      { url: "https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=800&q=80", alt: "Cleansing Balm" },
    ],
    options: [{ name: "Size", values: ["85g"] }],
    position: 2,
  }),
  mockProduct({
    id: "sp-bt-4",
    slug: "barrier-repair-creme",
    name: "Barrier Repair Crème",
    description: "Ceramides, squalane and centella asiatica to soothe, calm and restore stressed skin.",
    priceMinor: 5400,
    collection: "Moisturizers",
    images: [
      { url: "https://images.unsplash.com/photo-1571781926291-c477ebfd024b?auto=format&fit=crop&w=800&q=80", alt: "Barrier Repair Crème" },
    ],
    options: [{ name: "Size", values: ["50ml"] }],
    position: 3,
  }),
];

const HOME_PRODUCTS: Product[] = [
  mockProduct({
    id: "sp-hm-1",
    slug: "architectural-desk-lamp",
    name: "Architectural Desk Lamp",
    description: "Brushed brass and spun aluminum table light with 3-stage dimming and warm 2700K LED glow.",
    priceMinor: 14500,
    compareAtMinor: 18000,
    collection: "Lighting",
    images: [
      { url: "https://images.unsplash.com/photo-1507473885765-e6ed057f782c?auto=format&fit=crop&w=800&q=80", alt: "Architectural Desk Lamp" },
    ],
    options: [{ name: "Finish", values: ["Brushed Brass", "Matte Black", "Spun Steel"] }],
    position: 0,
  }),
  mockProduct({
    id: "sp-hm-2",
    slug: "hand-thrown-ceramic-dripper",
    name: "Hand-Thrown Ceramic Coffee Dripper",
    description: "Slow coffee ceramic pourover cone with ribbed interior for optimal extraction and clarity.",
    priceMinor: 4800,
    collection: "Kitchen",
    images: [
      { url: "https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?auto=format&fit=crop&w=800&q=80", alt: "Ceramic Dripper" },
    ],
    options: [{ name: "Glaze", values: ["Speckled Sand", "Matte Stone", "Charcoal"] }],
    position: 1,
  }),
  mockProduct({
    id: "sp-hm-3",
    slug: "organic-linen-throw",
    name: "Organic Washed Linen Throw",
    description: "Pre-washed European flax linen throw blanket with delicate fringe detailing. 130 x 170 cm.",
    priceMinor: 11000,
    collection: "Living",
    images: [
      { url: "https://images.unsplash.com/photo-1584100936595-c0654b55a2e2?auto=format&fit=crop&w=800&q=80", alt: "Linen Throw" },
    ],
    options: [{ name: "Color", values: ["Oatmeal", "Moss", "Terracotta"] }],
    position: 2,
  }),
  mockProduct({
    id: "sp-hm-4",
    slug: "natural-oak-stool",
    name: "Natural Oak Craft Stool",
    description: "Solid sustainably-sourced European white oak with traditional mortise-and-tenon joinery.",
    priceMinor: 17500,
    collection: "Furniture",
    images: [
      { url: "https://images.unsplash.com/photo-1503602642458-232111445657?auto=format&fit=crop&w=800&q=80", alt: "Oak Stool" },
    ],
    options: [{ name: "Finish", values: ["Natural Oil", "Smoked Oak"] }],
    position: 3,
  }),
];

export function getSampleStoreForTemplate(detail: TemplateDetail): PublicStore {
  const bizName = detail.sampleBusiness?.name || detail.name || "Curated Store";
  const slug = detail.id || "store-sample";
  const nameLower = detail.name.toLowerCase();

  let products = APPAREL_PRODUCTS;
  if (nameLower.includes("glow") || nameLower.includes("serum") || nameLower.includes("beauty")) {
    products = BEAUTY_PRODUCTS;
  } else if (nameLower.includes("nest") || nameLower.includes("oak") || nameLower.includes("loft") || nameLower.includes("living") || nameLower.includes("ecommerce")) {
    products = [...HOME_PRODUCTS, ...APPAREL_PRODUCTS.slice(0, 3)];
  }

  return {
    siteId: detail.id,
    name: bizName,
    slug,
    category: detail.categories?.[0] ?? "fashion",
    currency: "USD",
    deliveryOptions: [
      { id: "std", name: "Standard Shipping (3-5 business days)", feeMinor: 500, freeOverMinor: 5000 },
      { id: "exp", name: "Express Shipping (1-2 business days)", feeMinor: 1500 },
      { id: "pickup", name: "In-Store Pickup", feeMinor: 0, pickup: true },
    ],
    paymentMethods: ["card", "apple_pay", "google_pay"],
    productLayout: "classic",
    cardShape: "portrait",
    autoAdvancePhotos: true,
    products,
  };
}
